import { IProject } from '_interfaces/project';
import { syncedSelectedProjectAtom } from 'atoms/projects';
import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useGetProjectByIdQuery } from 'redux-store/api/projects';

/**
 * Custom hook that syncs the selected project from Redux with the Jotai atom.
 * This ensures that when a page refreshes, the selectedProjectAtom is populated
 * with the current project data from the URL parameter or provided projectId.
 *
 * @param projectId - Optional project ID. If not provided, will use projectId from URL params
 * @returns The project data and loading state from Redux
 */
export const useSyncSelectedProject = (projectId?: number | string) => {
  const { projectId: urlProjectId } = useParams<'projectId'>();
  const setSelectedProject = useSetAtom(syncedSelectedProjectAtom);

  // Use provided projectId or fall back to URL params
  const currentProjectId = projectId || urlProjectId;

  const {
    data: project,
    isLoading,
    isError,
  } = useGetProjectByIdQuery({ id: Number(currentProjectId) }, { skip: !currentProjectId });

  // Sync the Redux project data with the Jotai atom
  useEffect(() => {
    if (project) {
      const projectInstance = IProject.fromReduxData(project);
      setSelectedProject(projectInstance);
    }
  }, [project, setSelectedProject]);

  return {
    project: project ? IProject.fromReduxData(project) : null,
    isLoading,
    isError,
  };
};
